import { Link } from 'react-router-dom';
import { ArrowRight, Star, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { BRAND, FOUNDER } from '../../data/mock';

import { img, srcSet } from '../../lib/img';

export default function Hero() {
  return (
    <section className="relative min-h-[100svh] md:min-h-screen bg-ivory pt-24 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center pt-12 lg:pt-20">
        {/* Left content */}
        <div className="lg:col-span-7 relative z-10">
          <div className="flex items-center gap-3 mb-8 animate-fade-in">
            <span className="h-px w-12 bg-champagne-deep" />
            <span className="text-[11px] tracking-[0.3em] uppercase text-champagne-deep">{BRAND.name} — Men's Image Consultant, Bengaluru</span>
          </div>

          <h1 className="font-serif text-[44px] sm:text-[58px] lg:text-[78px] leading-[1.02] tracking-[-0.02em] text-charcoal animate-fade-up">
            Dress like the man <br className="hidden sm:block" />you <span className="italic-accent">want to become.</span>
          </h1>

          <p className="mt-8 text-base lg:text-lg text-charcoal/70 max-w-2xl leading-relaxed animate-fade-up" style={{animationDelay:'0.15s'}}>
            Personal Styling, Wardrobe Transformation and International Personal Shopping for ambitious men who want to look sharper, feel more confident and make stronger first impressions.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up" style={{animationDelay:'0.3s'}}>
            <Link to="/book-consultation">
              <Button data-testid="hero-book-btn" className="group bg-charcoal hover:bg-charcoal-soft text-ivory rounded-none px-8 py-7 text-[11px] tracking-[0.22em] uppercase">
                Book A Style Consultation
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/client-transformations">
              <Button data-testid="hero-transformations-btn" variant="outline" className="border-charcoal text-charcoal hover:bg-charcoal hover:text-ivory rounded-none px-8 py-7 text-[11px] tracking-[0.22em] uppercase">
                View Client Transformations
              </Button>
            </Link>
          </div>

          <div className="mt-5 flex items-center gap-3 text-[11px] tracking-[0.22em] uppercase text-charcoal/70 animate-fade-up" style={{animationDelay:'0.4s'}}>
            <span className="h-px w-6 bg-champagne-deep" />
            <span data-testid="hero-pricing-entry">
              Style Diagnosis from <span className="text-charcoal font-display tracking-normal text-base">&#8377;7,500</span>
            </span>
          </div>

          <div className="mt-14 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10 animate-fade-up" style={{animationDelay:'0.45s'}}>
            <div className="flex items-center gap-3">
              <div className="flex text-champagne-deep">
                {Array.from({length:5}).map((_,i)=> <Star key={i} size={14} fill="currentColor" />)}
              </div>
              <span className="text-xs tracking-wider text-charcoal/70">Trusted by Executives, Founders &amp; Professionals</span>
            </div>
            <a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} className="flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-charcoal/80 hover:text-champagne-deep transition-colors">
              <Phone size={14} /> {BRAND.phone}
            </a>
          </div>
        </div>

        {/* Right image - founder editorial */}
        <div className="lg:col-span-5 relative animate-fade-in">
          <div className="relative aspect-[3/4] w-full overflow-hidden">
            <img
              src={img(FOUNDER.editorial, 1000)}
              srcSet={srcSet(FOUNDER.editorial, [480, 800, 1200])}
              sizes="(max-width: 1024px) 100vw, 42vw"
              alt={`${FOUNDER.name} — editorial portrait`}
              className="w-full h-full object-cover"
              fetchPriority="high"
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal-deep/40 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div>
                <span className="block text-ivory text-[10px] tracking-[0.3em] uppercase">Anitya Vatsal</span>
                <span className="block text-champagne text-[10px] tracking-[0.25em] mt-1 uppercase">Founder · Style Consultant</span>
              </div>
              <span className="text-champagne text-xs font-serif italic">No. 01</span>
            </div>
          </div>
          <div className="absolute -bottom-6 -left-6 hidden md:block bg-ivory border border-ivory-deep px-6 py-5 max-w-[260px] shadow-sm">
            <p className="font-serif text-lg leading-snug text-charcoal">
              “Style is intentional. <em className="italic-accent">Impact is timeless.</em>”
            </p>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="mt-24 lg:mt-32 border-y border-charcoal/10 py-5 overflow-hidden">
        <div className="flex gap-12 whitespace-nowrap animate-marquee">
          {Array.from({length: 2}).map((_, k) => (
            <div key={k} className="flex gap-12 items-center">
              {['Founders','Executives','Grooms','CXOs','Public Speakers','Entrepreneurs','Consultants','High Performers'].map((t,i)=>(
                <span key={i} className="flex items-center gap-12">
                  <span className="font-display text-2xl tracking-[0.25em] text-charcoal">{t}</span>
                  <span className="text-champagne-deep">❖</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
